<template>
  <div class="wallet-wrapper">
    <!-- WP pill (always visible) -->
    <div class="wp-wrapper" :class="{ 'show-wp': hasWP }">
      <WhalePointsPill :whalePoints="whalePoints" />
    </div>

    <!-- Token pill -->
    <div class="token-wrapper">
      <TokenPill :caseToken="profile.caseToken" />
    </div>

    <!-- Wallet / Balance -->
    <div
      class="wallet-card"
      :title="t('HEADER.WALLET.refill')"
      :aria-label="t('HEADER.WALLET.aria.openRefill')"
      @click="handleOpenRefill"
    >
      <div class="wc-left">
        <div class="wc-info">
          <div class="wc-amount">
            <Price :value="profile.balance" />
          </div>
          <div class="wc-label">{{ t('HEADER.WALLET.balanceLabel') }}</div>
        </div>
      </div>

      <div class="wc-right" aria-hidden="true">
        <i class="fa-solid fa-wallet"></i>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useProfileStore } from '@/store/profile'
import { useAppStore } from '@/store/app'
import { useDepositStore } from '@/store/deposit'
import { usePromotionalEventsStore } from '@/store/promotionalEvents'
import { trackDepositInitiate } from '@/utils/gtagEvent'
// Components
import Price from '@/components/Price/Price.vue'
import TokenPill from '@/components/TokenPill/TokenPill.vue'
import WhalePointsPill from '@/components/WhalePointsPill/WhalePointsPill.vue'
import { useI18n } from 'vue-i18n'

const { t, n } = useI18n()

defineOptions({ name: 'Wallet' })

const profileStore = useProfileStore()
const { profile } = storeToRefs(profileStore)

const eventsStore = usePromotionalEventsStore()
const { hasActiveEvent } = storeToRefs(eventsStore)

const app = useAppStore()
const depositStore = useDepositStore()

const hasTokens = computed(() => (profile.value?.caseToken ?? 0) > 0)
const hasWP = computed(() => hasActiveEvent.value && (profile.value?.whalePoints ?? 0) > 0)
const whalePoints = computed(() => profile.value?.whalePoints ?? 0)

function handleOpenRefill() {
  trackDepositInitiate({ method: 'crypto', amount: 0, bonus_code_used: false })
  depositStore.setDepositMethod('crypto-payments')
  app.setShowDepositModal(true)
}
</script>

<style scoped lang="scss">
.wallet-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;

  .wp-wrapper {
    @include tablet {
      display: none;

      &.show-wp {
        display: block;
      }
    }

    @include mobile {
      display: none;

      &.show-wp {
        display: none;
      }
    }
  }

  .token-wrapper {
    @include mobile {
      display: none;
    }
  }

  .wallet-card {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 4px 10px;
    height: fit-content;
    background: $wallet-bg;
    border: 2px solid rgba(0, 0, 0, 0.35);
    border-radius: 8px;
    cursor: pointer;
    box-shadow:
      inset 0 0 0 1px rgba(255, 255, 255, 0.06),
      0 2px 8px rgba(0, 0, 0, 0.15);
    transition:
      background-color 0.18s ease,
      transform 0.06s ease;

    &:hover {
      background: $wallet-bg-hover;
    }
    &:active {
      transform: translateY(1px);
    }

    .wc-left {
      display: flex;
      align-items: center;
      gap: 10px;
    }

    .wc-badge {
      width: 28px;
      height: 28px;
      border-radius: 6px;
      background: $accent-lime;
      color: #111;
      font-weight: 600;
      display: grid;
      place-items: center;
      line-height: 1;
      flex: 0 0 28px;
    }

    .wc-info {
      display: flex;
      flex-direction: column;
      line-height: 1.1;

      .wc-amount {
        font-weight: 800;
        color: #e9ff9c;
        letter-spacing: 0.02em;
        font-size: 14px;
      }

      .wc-label {
        margin-top: 2px;
        font-size: 10px;
        font-weight: 700;
        color: $text-dim;
        letter-spacing: 0.08em;
        text-transform: uppercase;
      }
    }

    .wc-right {
      width: 32px;
      height: 32px;
      border-radius: 6px;
      background: $accent-lime;
      display: grid;
      place-items: center;
      color: #111;
      flex: 0 0 32px;
      box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.1);

      i {
        font-size: 14px;
      }
    }
  }

  @include mobile {
    gap: 4px;

    .wallet-card {
      padding: 2px 6px;
      gap: 6px;

      .wc-info {
        .wc-amount {
          font-size: 0.85rem;
        }

        .wc-label {
          display: none;
        }
      }

      .wc-right {
        width: 24px;
        height: 24px;
        flex: 0 0 24px;

        i {
          font-size: 11px;
        }
      }
    }
  }
}
</style>
